import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { colorTokens, spacingTokens, borderRadiusTokens, typographyTokens, shadowTokens, animationTokens } from "../config/designTokens";
import { useAuth } from "./AuthContext";

// Configuration constants
const SESSION_TIMEOUT_MS = 24 * 60 * 60 * 1000; // 24 hours absolute session duration
const WARNING_BEFORE_TIMEOUT_MS = 5 * 60 * 1000; // Show warning 5 minutes before timeout
const ACTIVITY_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart", "click"];
const SESSION_EXPIRATION_KEY = "session_expiration";
const SESSION_CHANNEL_NAME = "greenback_session_activity";

interface SessionTimeoutContextType {
  resetTimer: () => void;
  remainingTime: number;
  isWarningShown: boolean;
}

const SessionTimeoutContext = createContext<SessionTimeoutContextType | undefined>(undefined);

// Styled components for the warning modal
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { 
    opacity: 0; 
    transform: translate(-50%, -50%) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translate(-50%, -50%) scale(1); 
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  animation: ${fadeIn} ${animationTokens.duration.short} ${animationTokens.easing.easeOut};
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: ${colorTokens.light.background.paper};
  border-radius: ${borderRadiusTokens.md};
  box-shadow: ${shadowTokens.lg};
  width: 90%;
  max-width: 400px;
  padding: ${spacingTokens.xxl};
  z-index: 10000;
  animation: ${slideUp} ${animationTokens.duration.standard} ${animationTokens.easing.easeOut};
`;

const WarningIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto ${spacingTokens.lg};
  background-color: ${colorTokens.light.neutral.grey[200]};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
`;

const Title = styled.h2`
  font-family: ${typographyTokens.fontFamily.bold};
  font-size: ${typographyTokens.fontSize.title};
  font-weight: ${typographyTokens.fontWeight.bold};
  color: ${colorTokens.light.text.primary};
  text-align: center;
  margin: 0 0 ${spacingTokens.md} 0;
`;

const Message = styled.p`
  font-family: ${typographyTokens.fontFamily.primary};
  font-size: ${typographyTokens.fontSize.body};
  color: ${colorTokens.light.text.secondary};
  text-align: center;
  margin: 0 0 ${spacingTokens.lg} 0;
  line-height: 1.5;
`;

const Timer = styled.div`
  font-family: ${typographyTokens.fontFamily.bold};
  font-size: ${typographyTokens.fontSize.heading};
  font-weight: ${typographyTokens.fontWeight.bold};
  color: ${colorTokens.light.text.primary};
  text-align: center;
  margin: 0 0 ${spacingTokens.xl} 0;
  padding: ${spacingTokens.md};
  background-color: ${colorTokens.light.neutral.grey[100]};
  border-radius: ${borderRadiusTokens.sm};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${spacingTokens.md};
`;

const Button = styled.button<{ $variant?: "primary" | "secondary" }>`
  flex: 1;
  padding: ${spacingTokens.md} ${spacingTokens.lg};
  font-family: ${typographyTokens.fontFamily.medium};
  font-size: ${typographyTokens.fontSize.body};
  font-weight: ${typographyTokens.fontWeight.medium};
  border-radius: ${borderRadiusTokens.sm};
  cursor: pointer;
  transition: all ${animationTokens.duration.short} ${animationTokens.easing.easeInOut};

  ${props => props.$variant === "primary" ? `
    background-color: ${colorTokens.light.brand.primary};
    color: ${colorTokens.light.text.inverse};
    border: none;

    &:hover {
      background-color: ${colorTokens.light.brand.secondary};
    }
  ` : `
    background-color: transparent;
    color: ${colorTokens.light.text.primary};
    border: 1px solid ${colorTokens.light.border.default};

    &:hover {
      background-color: ${colorTokens.light.background.subtle};
    }
  `}
`;

// Format seconds to mm:ss
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const SessionTimeoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout, expireSession } = useAuth();
  const [remainingTime, setRemainingTime] = useState(SESSION_TIMEOUT_MS);
  const [isWarningShown, setIsWarningShown] = useState(false);

  const lastActivityRef = useRef<number>(Date.now());
  const sessionExpirationRef = useRef<number | null>(null);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  // Initialize session expiration from localStorage or create new one
  useEffect(() => {
    if (!user?.authToken) {
      sessionExpirationRef.current = null;
      return;
    }

    try {
      const storedExpiration = localStorage.getItem(SESSION_EXPIRATION_KEY);
      if (storedExpiration) {
        sessionExpirationRef.current = parseInt(storedExpiration, 10);
      } else {
        // Set new session expiration (24 hours from now)
        const expiration = Date.now() + SESSION_TIMEOUT_MS;
        sessionExpirationRef.current = expiration;
        localStorage.setItem(SESSION_EXPIRATION_KEY, expiration.toString());
      }
    } catch (error) {
      console.warn("Error accessing session expiration:", error);
    }
  }, [user?.authToken]);

  // Reset timer on activity
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIsWarningShown(false);
  }, []);

  // Broadcast activity to other tabs
  const broadcastActivity = useCallback(() => {
    if (broadcastChannelRef.current) {
      try {
        broadcastChannelRef.current.postMessage({ type: "activity", timestamp: Date.now() });
      } catch (error) {
        console.warn("Error broadcasting activity:", error);
      }
    }
  }, []);

  // Handle stay logged in
  const handleStayLoggedIn = useCallback(() => {
    resetTimer();
    broadcastActivity();
  }, [resetTimer, broadcastActivity]);

  // Handle logout now
  const handleLogoutNow = useCallback(async () => {
    setIsWarningShown(false);
    await logout();
  }, [logout]);

  // Set up BroadcastChannel for cross-tab communication
  useEffect(() => {
    if (!user?.authToken) {
      return;
    }

    try {
      // Create broadcast channel for cross-tab activity sync
      broadcastChannelRef.current = new BroadcastChannel(SESSION_CHANNEL_NAME);

      // Listen for activity messages from other tabs
      broadcastChannelRef.current.onmessage = (event) => {
        if (event.data.type === "activity") {
          // Update last activity time based on other tabs' activity
          lastActivityRef.current = event.data.timestamp;
          setIsWarningShown(false);
        } else if (event.data.type === "logout") {
          // Sync logout across tabs
          logout();
        }
      };
    } catch (error) {
      console.warn("BroadcastChannel not supported:", error);
    }

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
    };
  }, [user?.authToken, logout]);

  // Set up activity listeners and timer
  useEffect(() => {
    if (!user?.authToken) {
      return;
    }

    // Activity listener
    const handleActivity = () => {
      if (!isWarningShown) {
        lastActivityRef.current = Date.now();
        broadcastActivity();
      }
    };

    // Add event listeners
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Check timer every second
    intervalIdRef.current = setInterval(() => {
      const now = Date.now();

      // Check absolute session expiration first
      // Running out of time is not choosing to leave: field presses still
      // waiting to be sent are kept for the next sign-in (PRD 108 §4.2).
      if (sessionExpirationRef.current && now >= sessionExpirationRef.current) {
        expireSession();
        return;
      }

      // Calculate remaining time until absolute expiration
      const absoluteRemaining = sessionExpirationRef.current
        ? Math.max(0, sessionExpirationRef.current - now)
        : SESSION_TIMEOUT_MS;

      setRemainingTime(absoluteRemaining);

      // Show warning when approaching timeout
      if (absoluteRemaining <= WARNING_BEFORE_TIMEOUT_MS && absoluteRemaining > 0) {
        setIsWarningShown(true);
      }

      // Logout when timeout reached
      if (absoluteRemaining === 0) {
        expireSession();
      }
    }, 1000);

    return () => {
      // Cleanup
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, [user?.authToken, expireSession, isWarningShown, broadcastActivity]);

  // Context value
  const contextValue = {
    resetTimer,
    remainingTime,
    isWarningShown
  };

  // Calculate remaining seconds for display
  const remainingSeconds = Math.ceil(remainingTime / 1000);

  return (
    <SessionTimeoutContext.Provider value={contextValue}>
      {children}

      {isWarningShown && user?.authToken && (
        <>
          <ModalOverlay onClick={handleStayLoggedIn} />
          <ModalContainer>
            <WarningIcon>⏰</WarningIcon>
            <Title>Session Expiring</Title>
            <Message>
              Your session is about to expire due to inactivity.
              Would you like to stay logged in?
            </Message>
            <Timer>{formatTime(remainingSeconds)}</Timer>
            <ButtonContainer>
              <Button $variant="secondary" onClick={handleLogoutNow}>
                Log Out
              </Button>
              <Button $variant="primary" onClick={handleStayLoggedIn}>
                Stay Logged In
              </Button>
            </ButtonContainer>
          </ModalContainer>
        </>
      )}
    </SessionTimeoutContext.Provider>
  );
};

// Hook to access session timeout context
export const useSessionTimeout = () => {
  const context = useContext(SessionTimeoutContext);
  if (context === undefined) {
    throw new Error("useSessionTimeout must be used within a SessionTimeoutProvider");
  }
  return context;
};

export default SessionTimeoutProvider;
